import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Package, Edit3, Trash2, Search } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import Currency from '../Currency'
import { productService } from '../../services/api'
import { getFeaturedImageUrl } from '../../utils/imageUtils'

const ProductsTab = ({ 
  onAddProduct, 
  onEditProduct, 
  onDeleteProduct 
}) => {
  const [page, setPage] = useState(1)
  const [allProducts, setAllProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('desc')
  const pageLimit = 12

  const { data: productsResponse, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['admin-products-paginated', page, searchTerm, sortBy, sortOrder],
    queryFn: () => productService.getProducts({ 
      status: '', // Show all products (published and draft)
      page: page, 
      limit: pageLimit,
      search: searchTerm,
      sort: sortBy,
      order: sortOrder
    }),
    keepPreviousData: true
  })

  // Update allProducts when new data is fetched
  React.useEffect(() => {
    if (productsResponse) {
      if (page === 1) {
        setAllProducts(productsResponse.products || [])
      } else {
        setAllProducts(prev => [...prev, ...(productsResponse.products || [])])
      }
    }
  }, [productsResponse, page])

  // Reset pagination when search or sort changes
  React.useEffect(() => {
    setPage(1)
    setAllProducts([])
  }, [searchTerm, sortBy, sortOrder])

  const handleLoadMore = () => {
    if (productsResponse?.pagination && page < productsResponse.pagination.pages) {
      setPage(prev => prev + 1)
    }
  }

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleSortChange = (e) => {
    const [sort, order] = e.target.value.split('-')
    setSortBy(sort)
    setSortOrder(order)
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  const currentCount = allProducts.length
  const totalCount = productsResponse?.pagination?.total || 0
  const hasMoreProducts = productsResponse?.pagination && page < productsResponse.pagination.pages

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h2 className="text-xl font-semibold text-gray-900">إدارة المنتجات</h2>
        <button 
          onClick={onAddProduct}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          إضافة منتج جديد
        </button>
      </div>

      {/* Search and Sort Controls */}
      <div className="bg-white p-4 rounded-lg border space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between gap-4">
        {/* Search Bar */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="البحث في المنتجات..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">ترتيب حسب:</label>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={handleSortChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="id-desc">المعرف (الأحدث أولاً)</option>
              <option value="id-asc">المعرف (الأقدم أولاً)</option>
              <option value="name-asc">الاسم (أ-ي)</option>
              <option value="name-desc">الاسم (ي-أ)</option>
              <option value="created_at-desc">تاريخ الإنشاء (الأحدث)</option>
              <option value="created_at-asc">تاريخ الإنشاء (الأقدم)</option>
              <option value="price-desc">السعر (الأعلى أولاً)</option>
              <option value="price-asc">السعر (الأقل أولاً)</option>
            </select>
          </div>
          
          {/* Results Count */}
          <div className="text-sm text-gray-500">
            {searchTerm ? `نتائج البحث: ${totalCount}` : `إجمالي: ${totalCount}`}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {productsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <Package className="h-5 w-5 text-red-400" />
            </div>
            <div className="mr-3">
              <h3 className="text-sm font-medium text-red-800">
                خطأ في تحميل المنتجات
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{productsError.message || productsError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state for first page */}
      {productsLoading && page === 1 && (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Products table */}
      {allProducts && allProducts.length > 0 && (
        <div className="bg-white shadow rounded-lg border">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    المعرف
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    المنتج
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    السعر
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    الحالة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allProducts.map((product) => (
                  <tr key={product.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 hidden md:table-cell">
                      #{product.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {getFeaturedImageUrl(product.featured_image) ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={getFeaturedImageUrl(product.featured_image)}
                              alt={product.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-xs text-gray-500">لا</span>
                            </div>
                          )}
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          <div className="text-sm text-gray-500">{product.category_name}</div>
                          {/* Mobile-only additional info */}
                          <div className="md:hidden text-xs text-gray-500 mt-1">
                            <div>#{product.id}</div>
                            <div>السعر: <Currency amount={product.price} /></div>
                            <div className="mt-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                product.status === 'published' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {product.status === 'published' ? 'منشور' : 'مسودة'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                      <Currency amount={product.price} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.status === 'published' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {product.status === 'published' ? 'منشور' : 'مسودة'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-col space-y-1 md:flex-row md:space-y-0 md:space-x-2">
                        <button
                          onClick={() => onEditProduct(product.id)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                          title="تعديل المنتج"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDeleteProduct(product.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                          title="حذف المنتج"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {hasMoreProducts && (
            <div className="bg-white px-4 py-6 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col items-center">
                <p className="text-sm text-gray-700 mb-4">
                  عرض {currentCount} من {totalCount} منتج
                </p>
                <button
                  onClick={handleLoadMore}
                  disabled={productsLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {productsLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري التحميل...
                    </>
                  ) : (
                    <>
                      <Plus className="h-5 w-5 mr-2" />
                      تحميل المزيد
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Show total count when all products are loaded */}
          {!hasMoreProducts && currentCount > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
              <p className="text-center text-sm text-gray-600">
                تم عرض جميع المنتجات ({totalCount} منتج)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!productsLoading && allProducts.length === 0 && (
        <div className="text-center py-12">
          <Package className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">لا توجد منتجات</h3>
          <p className="mt-1 text-sm text-gray-500">ابدأ بإضافة منتج جديد</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={onAddProduct}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-5 w-5 mr-2" />
              إضافة منتج جديد
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductsTab
